import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Dumbbell, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SchemasTab({ token }) {
  const [schemas, setSchemas] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null); // eslint-disable-line no-unused-vars
  const [showCreate, setShowCreate] = useState(false); // eslint-disable-line no-unused-vars
  const [naam, setNaam] = useState(""); // eslint-disable-line no-unused-vars
  const [doel, setDoel] = useState(""); // eslint-disable-line no-unused-vars
  const [beschrijving, setBeschrijving] = useState(""); // eslint-disable-line no-unused-vars
  const [folderId, setFolderId] = useState(""); // eslint-disable-line no-unused-vars
  const [createLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [createError] = useState(""); // eslint-disable-line no-unused-vars

  const showToast = (msg, type = "success") => setToast({ msg, type }); // eslint-disable-line no-unused-vars

  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, fr] = await Promise.all([
        fetch(`${API}/api/company/custom-schemas`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/company/schema-folders`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setSchemas(sr.schemas || []);
      setFolders(fr.folders || []);
    } catch { showToast("Laden mislukt", "error"); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/api/company/custom-schemas/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      showToast("Schema verwijderd");
      load();
    } catch { showToast("Verwijderen mislukt", "error"); }
  };

  const getFolderNaam = (fid) => folders.find(f => f.id === fid)?.naam || "Geen map";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Bibliotheek</p>
          <h2 className="text-xl font-bold text-white">Bedrijfsschema's</h2>
        </div>
        <button
          onClick={() => navigate("/bedrijf-schema-editor")}
          data-testid="add-schema-btn"
          className="flex items-center gap-2 px-4 py-2.5 text-black font-bold text-sm rounded-xl"
          style={{ background: "#C9A84C" }}
        >
          <Plus size={14} /> Schema aanmaken
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-600"><RefreshCw size={20} className="animate-spin mx-auto" /></div>
      ) : schemas.length === 0 ? (
        <div className="text-center py-16">
          <Dumbbell size={40} className="text-zinc-800 mx-auto mb-3" />
          <p className="text-zinc-500 font-semibold mb-1">Nog geen schema's</p>
          <button onClick={() => navigate("/bedrijf-schema-editor")} className="mt-4 px-5 py-2.5 text-black font-bold text-sm rounded-xl" style={{ background: "#C9A84C" }}>
            Eerste schema aanmaken
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {schemas.map(s => (
            <div key={s.id} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-sm truncate">{s.naam}</h3>
                  {s.doel && <p className="text-zinc-500 text-xs mt-0.5">{s.doel}</p>}
                </div>
                <button onClick={() => handleDelete(s.id)} className="p-1 text-zinc-700 hover:text-red-400 transition-colors ml-2 flex-shrink-0">
                  <Trash2 size={13} />
                </button>
              </div>
              {s.beschrijving && <p className="text-zinc-600 text-xs mb-3 line-clamp-2">{s.beschrijving}</p>}
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-blue-500/10 text-blue-400">{s.niveau || "Beginner"}</span>
                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-zinc-700 text-white">{s.trainingsdagen || 3}× p/w</span>
                {s.folder_id && <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#C9A84C]/10 text-[#C9A84C]">{getFolderNaam(s.folder_id)}</span>}
                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-zinc-700 text-white">{(s.dagen || []).length} dagen</span>
              </div>
              <button
                onClick={() => navigate(`/bedrijf-schema-editor/${s.id}`)}
                data-testid={`edit-schema-btn-${s.id}`}
                className="w-full py-2 border border-white/8 rounded-xl text-zinc-400 hover:text-white hover:border-[#C9A84C]/30 text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
              >
                <Plus size={11} /> Oefeningen bewerken
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
